import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useLang } from "@/i18n";
import { useSite } from "@/context/SiteContext";

export default function Footer() {
  const { t, lang } = useLang();
  const { site } = useSite();
  const address = site?.[`address_${lang}`] || t.office_address;
  const phone = site?.phone || "9324899996";
  const email = site?.email || "samatapartymaharashtra@zohomail.in";
  const socials = {
    twitter: site?.twitter || "https://twitter.com/samataparty_ss",
    instagram: site?.instagram || "https://instagram.com/samataparty_",
    facebook: site?.facebook || "https://www.facebook.com/share/1DYveGmDU9/",
    youtube: site?.youtube || "https://youtube.com/@samatachannel",
  };

  return (
    <footer className="bg-emerald-950 text-emerald-100" data-testid="main-footer">
      <div className="tricolor-bar w-full" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.jpeg" alt="Samata Party" className="w-12 h-12 rounded-full bg-white object-contain p-0.5" />
            <div>
              <div className="font-heading font-bold text-lg text-white">{t.party_name}</div>
              <div className="text-xs text-amber-400 font-semibold">{t.party_state}</div>
            </div>
          </div>
          <p className="text-sm text-emerald-200 italic mb-2">"{t.tagline}"</p>
          <div className="flex gap-3 mt-4" data-testid="footer-social-links">
            <a href={socials.twitter} target="_blank" rel="noreferrer" data-testid="social-twitter-link"
              className="w-9 h-9 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-600 transition-colors">
              <Twitter className="w-4 h-4" />
            </a>
            <a href={socials.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram-link"
              className="w-9 h-9 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-600 transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href={socials.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook-link"
              className="w-9 h-9 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-600 transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={socials.youtube} target="_blank" rel="noreferrer" data-testid="social-youtube-link"
              className="w-9 h-9 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-600 transition-colors">
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-heading font-bold text-white mb-4">{t.quick_links}</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-amber-400 transition-colors" data-testid="footer-about-link">{t.nav_about}</Link></li>
            <li><Link to="/constitution" className="hover:text-amber-400 transition-colors" data-testid="footer-constitution-link">{t.nav_constitution}</Link></li>
            <li><Link to="/elections" className="hover:text-amber-400 transition-colors" data-testid="footer-elections-link">{t.nav_elections}</Link></li>
            <li><Link to="/rnvp" className="hover:text-amber-400 transition-colors" data-testid="footer-rnvp-link">{t.nav_rnvp}</Link></li>
            <li><Link to="/membership" className="hover:text-amber-400 transition-colors" data-testid="footer-membership-link">{t.nav_membership}</Link></li>
            <li><Link to="/donation" className="hover:text-amber-400 transition-colors" data-testid="footer-donation-link">{t.nav_donation}</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-heading font-bold text-white mb-4">{t.contact_title}</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
              <span data-testid="footer-address">{address}</span>
            </li>
            <li className="flex gap-2 items-center">
              <Phone className="w-4 h-4 shrink-0 text-amber-400" />
              <a href={`tel:${phone}`} className="hover:text-amber-400" data-testid="footer-phone-link">{phone}</a>
            </li>
            <li className="flex gap-2 items-center">
              <Mail className="w-4 h-4 shrink-0 text-amber-400" />
              <a href={`mailto:${email}`} className="hover:text-amber-400 break-all" data-testid="footer-email-link">
                {email}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-emerald-900 py-4 text-center text-xs text-emerald-300">
        © {new Date().getFullYear()} {t.party_name} — {t.party_state}. {t.rights}.
      </div>
    </footer>
  );
}
